import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  ClockIcon,
  ServerStackIcon,
  BoltIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import Dropdown from "../forms/Dropdown";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import Card from "../layout/Card";
import DangerZone from "../forms/DangerZone";
import {
  GroupState,
  createGroup,
  deleteGroups,
  getAppliances,
  getGroups,
  updateGroup,
} from "../../slices/smart_control";
import { getDevices } from "../../slices/devices";

export default function Groups() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getGroups());
    dispatch(getAppliances({ pageNum: false, model: null }));
    dispatch(getRooms());
    dispatch(getDevices({ pageNum: false, model: null }));
  }, [dispatch]);

  // Set state
  const [selectedGroup, setSelectedGroup] = useState<{
    id: string;
    name: string;
    devices: string[];
    appliances: string[];
    associated_room: string;
    status: string;
  }>({
    id: "",
    name: "",
    devices: [],
    appliances: [],
    associated_room: "",
    status: "",
  });
  const [createGroupForm, setCreateGroupForm] = useState<{
    name: string;
    devices: string[];
    appliances: string[];
    associated_room: string;
  }>({
    name: "",
    devices: [],
    appliances: [],
    associated_room: "",
  });
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { groups, appliances } = useAppSelector((state) => state.smartControl);
  const { rooms } = useAppSelector((state) => state.rooms);
  const { devices } = useAppSelector((state) => state.devices);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectGroupToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [groupId] = args;
    const groupData = groupsData.filter((group) => group.id === groupId)[0];
    setSelectedGroup({
      id: groupData.id,
      name: groupData.name,
      devices: groupData.devices.map((device) => device[0]),
      appliances: groupData.appliances.map((appliance) => appliance[0]),
      associated_room:
        groupData.associated_room === null ? "" : groupData.associated_room[0],
      status: groupData.status,
    });
    setShowEditModal(true);
  }

  function openCreateGroupModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function selectOrUnselectAllCreateDevices(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableDevices =
        devices.data === null ? [] : devices.data.map((x) => x.id);
      setCreateGroupForm({ ...createGroupForm, devices: allAvailableDevices });
    } else {
      setCreateGroupForm({ ...createGroupForm, devices: [] });
    }
  }

  function selectOrUnselectAllCreateAppliances(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableAppliances =
        appliances.data === null ? [] : appliances.data.map((x) => x.id);
      setCreateGroupForm({
        ...createGroupForm,
        appliances: allAvailableAppliances,
      });
    } else {
      setCreateGroupForm({ ...createGroupForm, appliances: [] });
    }
  }

  function selectOrUnselectAllEditDevices(e: any, args: any[]) {
    e.preventDefault();
    if (selectedGroup === null) return;
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableDevices =
        devices.data === null ? [] : devices.data.map((x) => x.id);
      setSelectedGroup({ ...selectedGroup, devices: allAvailableDevices });
    } else {
      setSelectedGroup({ ...selectedGroup, devices: [] });
    }
  }

  function selectOrUnselectAllEditAppliances(e: any, args: any[]) {
    e.preventDefault();
    if (selectedGroup === null) return;
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableAppliances =
        appliances.data === null ? [] : appliances.data.map((x) => x.id);
      setSelectedGroup({
        ...selectedGroup,
        appliances: allAvailableAppliances,
      });
    } else {
      setSelectedGroup({ ...selectedGroup, appliances: [] });
    }
  }

  async function confirmCreateGroup(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      name,
      devices,
      appliances,
      associated_room: associatedRoom,
    } = createGroupForm;

    // Validate text fields
    if (name === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (associatedRoom === "") {
      dispatch(
        addAlert(generateAlertText("missing_room", language), "danger", 5000),
      );
      return;
    }

    setShowCreateModal(false);
    const res: any = await dispatch(
      createGroup({
        name,
        property: user.property.id,
        devices,
        appliances,
        associated_room: associatedRoom,
        status: "CP",
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(generateAlertText("create_group", language), "success", 3000),
      );
      setCreateGroupForm({
        name: "",
        devices: [],
        appliances: [],
        associated_room: "",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditGroup(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      id,
      name,
      devices,
      appliances,
      associated_room: associatedRoom,
      status,
    } = selectedGroup;

    // Validate text fields
    if (name === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (associatedRoom === "") {
      dispatch(
        addAlert(generateAlertText("missing_room", language), "danger", 5000),
      );
      return;
    }

    setShowEditModal(false);
    const res: any = await dispatch(
      updateGroup({
        groupId: id,
        name,
        property: user.property.id,
        devices,
        appliances,
        associated_room: associatedRoom,
        status,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(generateAlertText("update_group", language), "success", 3000),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeleteGroup(e: any, args: any[]) {
    e.preventDefault();
    const [groupIds] = args;
    if (groupIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (groupIds === null || groupIds === undefined) {
      dispatch(
        addAlert(generateAlertText("missing_groups", language), "danger", 3000),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(deleteGroups({ groupIds: [groupIds] }));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function updateDeviceForCreateGroup(deviceId: string) {
    if (createGroupForm.devices.includes(deviceId)) {
      const updatedDevices = createGroupForm.devices.filter(
        (x) => x !== deviceId,
      );
      setCreateGroupForm({ ...createGroupForm, devices: updatedDevices });
    } else {
      const updatedDevices = [...createGroupForm.devices, deviceId];
      setCreateGroupForm({ ...createGroupForm, devices: updatedDevices });
    }
  }

  function updateDeviceForEditGroup(deviceId: string) {
    if (selectedGroup.devices.includes(deviceId)) {
      const updatedDevices = selectedGroup.devices.filter(
        (x) => x !== deviceId,
      );
      setSelectedGroup({ ...selectedGroup, devices: updatedDevices });
    } else {
      const updatedDevices = [...selectedGroup.devices, deviceId];
      setSelectedGroup({ ...selectedGroup, devices: updatedDevices });
    }
  }

  function updateApplianceForCreateGroup(applianceId: string) {
    if (createGroupForm.appliances.includes(applianceId)) {
      const updatedAppliances = createGroupForm.appliances.filter(
        (x) => x !== applianceId,
      );
      setCreateGroupForm({ ...createGroupForm, appliances: updatedAppliances });
    } else {
      const updatedAppliances = [...createGroupForm.appliances, applianceId];
      setCreateGroupForm({ ...createGroupForm, appliances: updatedAppliances });
    }
  }

  function updateApplianceForEditGroup(applianceId: string) {
    if (selectedGroup.appliances.includes(applianceId)) {
      const updatedAppliances = selectedGroup.appliances.filter(
        (x) => x !== applianceId,
      );
      setSelectedGroup({ ...selectedGroup, appliances: updatedAppliances });
    } else {
      const updatedAppliances = [...selectedGroup.appliances, applianceId];
      setSelectedGroup({ ...selectedGroup, appliances: updatedAppliances });
    }
  }

  function buildApplianceText(appliances: any[]) {
    return `${appliances.length} ${
      appliances.length === 1
        ? translateText("appliance", language, true)
        : translateText("appliances", language, true)
    }`;
  }

  function buildDevicesText(devices: any[]) {
    return `${devices.length} ${
      devices.length === 1
        ? translateText("device", language, true)
        : translateText("devices", language, true)
    }`;
  }

  // Format rooms data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const devicesData = devices.data === null ? [] : devices.data;
  const appliancesData = appliances.data === null ? [] : appliances.data;
  const groupsData = groups.data === null ? [] : groups.data;

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("groups_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateGroupModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_group", language)}
              </Button>
            }
          />
        }
      />

      {/* Create group modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_group", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateGroup}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_group", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInput
                id="name"
                type="text"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createGroupForm}
                stateName="name"
                onChange={setCreateGroupForm}
              />

              {/* Devices */}
              <>
                <div>
                  <label className="mb-1 block text-left text-sm font-medium text-black">
                    {translateText("devices", language)}
                  </label>
                </div>

                {/* Select/unselect all buttons */}
                <SelectOrUnselectAll
                  handler={selectOrUnselectAllCreateDevices}
                  language={language}
                />
                <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                  {devicesData.map((device: any) => (
                    <Checkbox
                      key={device.id}
                      id={device.id}
                      onChange={updateDeviceForCreateGroup}
                      checked={createGroupForm.devices.includes(device.id)}
                      label={device.name}
                    />
                  ))}
                </div>
              </>

              {/* Appliances */}
              <>
                <div>
                  <label className="mb-1 block text-left text-sm font-medium text-black">
                    {translateText("appliances", language)}
                  </label>
                </div>

                {/* Select/unselect all buttons */}
                <SelectOrUnselectAll
                  handler={selectOrUnselectAllCreateAppliances}
                  language={language}
                />
                <div className="grid md:grid-cols-2 grid-cols-1 gap-2">
                  {appliancesData.map((app: any) => (
                    <Checkbox
                      key={app.id}
                      id={app.id}
                      onChange={updateApplianceForCreateGroup}
                      checked={createGroupForm.appliances.includes(app.id)}
                      label={app.name}
                    />
                  ))}
                </div>
              </>

              {/* Associated room */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("associated_room", language)}
                showLabel={true}
                required={true}
                state={createGroupForm}
                stateName="associated_room"
                onChange={setCreateGroupForm}
                defaultValue=""
              >
                <option value=""></option>
                {roomsData.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </Dropdown>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit group modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_group", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditGroup}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_group", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInput
                id="name"
                type="text"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedGroup}
                stateName="name"
                onChange={setSelectedGroup}
              />

              {/* Devices */}
              <>
                <div>
                  <label className="mb-1 block text-left text-sm font-medium text-black">
                    {translateText("devices", language)}
                  </label>
                </div>

                {/* Select/unselect all buttons */}
                <SelectOrUnselectAll
                  handler={selectOrUnselectAllEditDevices}
                  language={language}
                />
                <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                  {devicesData.map((device: any) => (
                    <Checkbox
                      key={device.id}
                      id={device.id}
                      onChange={updateDeviceForEditGroup}
                      checked={selectedGroup.devices.includes(device.id)}
                      label={device.name}
                    />
                  ))}
                </div>
              </>

              {/* Appliances */}
              <>
                <div>
                  <label className="mb-1 block text-left text-sm font-medium text-black">
                    {translateText("appliances", language)}
                  </label>
                </div>

                {/* Select/unselect all buttons */}
                <SelectOrUnselectAll
                  handler={selectOrUnselectAllEditAppliances}
                  language={language}
                />
                <div className="grid md:grid-cols-2 grid-cols-1 gap-2">
                  {appliancesData.map((app: any) => (
                    <Checkbox
                      key={app.id}
                      id={app.id}
                      onChange={updateApplianceForEditGroup}
                      checked={selectedGroup.appliances.includes(app.id)}
                      label={app.name}
                    />
                  ))}
                </div>
              </>

              {/* Associated room */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("associated_room", language)}
                showLabel={true}
                required={true}
                state={selectedGroup}
                stateName="associated_room"
                onChange={setSelectedGroup}
                defaultValue={selectedGroup.associated_room}
              >
                {roomsData.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </Dropdown>

              {/* Danger zone */}
              <DangerZone
                objectId={selectedGroup.id}
                handler={confirmDeleteGroup}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {appliances.loading ||
      devices.loading ||
      groups.loading ||
      rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {groupsData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {groupsData.map((group: GroupState) => (
                    <a
                      href="#!"
                      key={group.id}
                      onClick={(e) => selectGroupToEdit(e, [group.id])}
                    >
                      {/* Group data */}
                      <Card
                        key={group.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {group.name}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <ServerStackIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {buildDevicesText(group.devices)}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <BoltIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {buildApplianceText(group.appliances)}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {group.datetime_created}
                          </div>
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_groups", language)}
                subtext={translateText("no_groups_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
